import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { putRequest } from '../../../requestUtils';
import { ABOUT_TEXT_SUGGESTION, IDEAL_CANDIDATE_SUGGESTION } from '../../../constants';
import {
  sanitizeResponsibilityHtml,
  responsibilitiesToListElements,
  sanitizeOnChangeValue,
  formatIdealCandidateOnLoad,
} from '../../../responsibilitiesUtils';
import ContentEditableComponent from '../../common/ContentEditableComponent';
import '../ProjectDetails.scss';

const MAX_RESPONSIBILITIES = 4; // the maximum number of responsibilities

const respArray = [{ description: '' }, { description: '' }];
const formStep = 'responsibilities';

class ProjectResponsibilities extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showModal: false,
      errors: null,
      responsibilities: respArray,
      numResponsibilities: respArray.length, // the number of active responsibilities (starts at 2)
      description: '',
      idealCandidateDescription: '',
      responsibilityAreas: '',
      status: '',
      descriptionDefault: false,
      idealCandidateDefault: false,

    };

    this.setResp = this.setResp.bind(this);
    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleHideModal = this.handleHideModal.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.addResp = this.addResp.bind(this);
    this.removeResp = this.removeResp.bind(this);
    this.validResponsibilities = this.validResponsibilities.bind(this);
    this.handleClearSuggestions = this.handleClearSuggestions.bind(this);
    this.validateFormInput = this.validateFormInput.bind(this);
  }

  componentDidMount() {
    const { projectResourceId, getProjectDetails } = this.props;

    if (!projectResourceId) return;

    getProjectDetails(formStep).then(({ data }) => {
      const responsibilitieslength = data.responsibility_areas.length;
      this.setState({
        status: data.status,
        descriptionDefault: data.status === 'employer_draft' && data.description === null,
        idealCandidateDefault: data.status === 'employer_draft' && (
          formatIdealCandidateOnLoad(data.ideal_candidate_description) === null),
        description: data.description || ABOUT_TEXT_SUGGESTION,
        idealCandidateDescription: formatIdealCandidateOnLoad(data.ideal_candidate_description)
          || IDEAL_CANDIDATE_SUGGESTION,
        numResponsibilities: responsibilitieslength || respArray.length,
        responsibilities: responsibilitieslength ? data.responsibility_areas : respArray,
        responsibilityAreas: responsibilitiesToListElements(data.responsibility_areas) || '<li></li>',
      });
    });
  }

  setResp(e, index) {
    const { responsibilities } = this.state;
    const newResp = responsibilities;
    newResp[index] = { description: e.target.value, id: e.target.dataset.id };
    this.setState({ responsibilities: newResp });
  }

  handleDescriptionChange(value) {
    this.setState({ errors: null, description: value });
  }

  handleListChange(event, inputName) {
    const original = String(event.target.value);

    // Sanitize the input before passing to sanitizeOnChangeValue
    let value = sanitizeOnChangeValue(original.replace(/&amp;/g, '&'));
    const newLength = value.length;
    // Check the length and apply additional transformations
    if (newLength > 0) {
      // Add <li> tags for bullet points if necessary
      if (!value.startsWith('<li>') && !value.endsWith('</li>')) {
        value = `<li>${value}</li>`;
      }
    }

    this.setState({ [inputName]: value, errors: null });
  }

  handleClearSuggestions(inputName) {
    if (inputName === 'description') {
      this.setState({ [inputName]: '' });
    } else {
      this.setState({ [inputName]: '<li></li>' });
    }
  }

  addResp() {
    const { responsibilities, numResponsibilities } = this.state;
    const newResp = [...responsibilities, { description: '' }];
    this.setState({ responsibilities: newResp, numResponsibilities: numResponsibilities + 1 });
  }

  removeResp(id, index) {
    const { responsibilities, numResponsibilities } = this.state;
    const newResp = responsibilities;
    if (responsibilities.length < 3) { // if there are only 2 responsibilities
      newResp[index] = { description: '', id }; // clear them rather than delete them
    } else {
      newResp[index] = { _destroy: 'true', id };
      this.setState({ numResponsibilities: numResponsibilities - 1 });
    }
    this.setState({ responsibilities: newResp });
  }

  validResponsibilities() {
    const { responsibilities, numResponsibilities } = this.state;

    let count = 0;

    if (numResponsibilities <= 1) return false;
    responsibilities.forEach((resp) => {
      if (!(resp._destroy === 'true') && resp.description.length > 2) {
        count += 1;
      }
    });

    return count > 1;
  }

  validateFormInput() {
    const {
      description, idealCandidateDescription, responsibilityAreas,
    } = this.state;
    const errors = {};
    [description, idealCandidateDescription, responsibilityAreas].forEach((input, i) => {
      const inputNames = ['description', 'idealCandidateDescription', 'responsibilityAreas'];

      if (input.length < 100) {
        errors[inputNames[i]] = 'should be more than 100 characters';
      }
    });

    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return true;
    }

    return false;
  }

  handleShowModal() {
    this.setState({ showModal: true });
  }

  handleHideModal() {
    this.setState({ showModal: false });
  }

  handleBack() {
    if (this.props.formType === 'page') {
      this.props.back();
    } else {
      this.handleHideModal();
    }
  }

  handleSubmit() {
    const { csrfToken, projectResourceId, afterSubmit } = this.props;
    const {
      responsibilities, description, idealCandidateDescription, responsibilityAreas,
    } = this.state;

    if (this.validateFormInput()) return;
    // converting responsibilities from an array to a nested hash
    const respHash = {};
    responsibilities.forEach((responsibility, i) => {
      if (responsibility) {
        respHash[i] = responsibility;
      }
    });

    const responsibilityAreaAttributes = sanitizeResponsibilityHtml(responsibilityAreas);

    putRequest(
      `/projects/${projectResourceId}`,
      {
        project: {
          ideal_candidate_description: idealCandidateDescription,
          responsibility_areas_attributes: responsibilityAreaAttributes,
          description,
        },
      },
      csrfToken,
    ).then(() => {
      afterSubmit();
    }).catch((error) => {
      this.setState({ errors: error.name });
    });
  }

  renderModal() {
    return (
      <Modal
        show={this.state.showModal}
        onHide={this.handleHideModal}
        dialogClassName="project-resp-modal"
      >
        <Modal.Header closeButton />
        <Modal.Body className="project-resp-modal-body">
          {this.renderContent()}
        </Modal.Body>
      </Modal>
    );
  }

  renderButtons() {
    const { backButtonText, submitButtonText } = this.props;
    const disabledState = '';

    return (
      <div className="footer-buttons-container">
        <button className="btn btn-secondary button-back" onClick={this.handleBack}>
          {backButtonText}
        </button>
        <button
          className={`btn btn-primary button-submit ${disabledState}`}
          onClick={this.handleSubmit}
          disabled={disabledState}
        >
          {submitButtonText}
        </button>
      </div>
    );
  }

  renderErrors() {
    const { errors } = this.state;

    return (errors
      && (
      <div id="error_explanation">
        <h3>Errors prohibited this project from being saved:</h3>
        <ul>
          {errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      </div>
      )
    );
  }

  renderResponsibilities() {
    const { responsibilities } = this.state;

    return (
      responsibilities.map((responsibility, index) => {
        if (responsibility._destroy === 'true') return null;
        return (
          <div>
            <div className="body-text-2 mb-2 mt-2">
              Responsibility
              {' '}
              {index + 1}
&nbsp;&nbsp;
              <a
                className="remove-resp"
                role="button"
                tabIndex={0}
                onClick={() => this.removeResp(responsibility.id, index)}
              >
                &#215;Remove
              </a>
            </div>
            <textarea
              name="responsibility-input"
              className="form-control fs-hide null"
              required="required"
              data-id={responsibility.id}
              onChange={(e) => { this.setResp(e, index); }}
              value={responsibility.description}
            />
          </div>
        );
      })
    );
  }

  renderAddResponsibility() {
    const { numResponsibilities } = this.state;

    if (numResponsibilities < MAX_RESPONSIBILITIES) {
      return (
        <div>
          <a
            className="add-resp body-text-2 medium-weight mt-2"
            role="button"
            tabIndex={0}
            onClick={() => this.addResp()}
          >
            + Add another responsibility
          </a>
          <p className="add-resp body-text-2 light-weight muted-gray-text mt-2">
            &nbsp;&nbsp;(Maximum 4 responsibilities)
          </p>
        </div>
      );
    }
    return null;
  }

  renderContent() {
    const {
      description, idealCandidateDescription, responsibilityAreas, errors,
      descriptionDefault, idealCandidateDefault,
    } = this.state;
    const suggestionText = 'We\'ve added copy suggestions';
    return (
      <div className="style-guide responsibility-container" key={this.state.numResponsibilities}>
        <div className="g-row">
          <div className="g-column g-flex-1" />
          <div className="g-column g-flex-60 responsibility-inputs">
            <h2 className="header">Description</h2>
            <ContentEditableComponent
              title="About This Project"
              description="Provide a summary of the company and the role."
              className="job-description"
              handleOnChange={(value) => this.handleDescriptionChange(value)}
              handleClearSuggestions={() => this.handleClearSuggestions('description')}
              showClearSuggestion={descriptionDefault}
              suggestionText={suggestionText}
              showError={errors && Object.keys(errors).includes('description')}
              value={description}
              requiredText="* Required"
              errorMessage={errors ? errors.description : null}
              required
            />
            <ContentEditableComponent
              title="Responsibilities"
              description={
                `Example: Manage and direct all clinical processes and
                operations relating to clinical data management for clinical trials.`
              }
              className="responsibilities"
              handleOnChange={(event) => this.handleListChange(event, 'responsibilityAreas')}
              handleOnPaste={(event) => this.handleOnPaste(event, 'responsibilityAreas')}
              handleClearSuggestions={() => this.handleClearSuggestions('responsibilityAreas')}
              value={responsibilityAreas}
              showError={errors && Object.keys(errors).includes('responsibilityAreas')}
              errorMessage={errors ? errors.responsibilityAreas : null}
              requiredText="* Required"
              withBulletPoints
              required
            />
            <ContentEditableComponent
              title="Ideal Consultant"
              description="Describe the phenotype or attributes your ideal candidate needs for this role."
              className="ideal-candidate"
              handleOnChange={(event) => this.handleListChange(event, 'idealCandidateDescription')}
              handleClearSuggestions={() => this.handleClearSuggestions('idealCandidateDescription')}
              handleOnPaste={(event) => this.handleOnPaste(event, 'idealCandidateDescription')}
              value={idealCandidateDescription}
              showClearSuggestion={idealCandidateDefault}
              suggestionText={suggestionText}
              showError={errors && Object.keys(errors).includes('idealCandidateDescription')}
              errorMessage={errors ? errors.idealCandidateDescription : null}
              requiredText="* Required"
              withBulletPoints
              required
            />
            {this.renderButtons()}
          </div>
        </div>
      </div>
    );
  }

  renderModalLink() {
    return (
      <i
        role="button"
        tabIndex={0}
        className="fa fa-edit modal-link"
        onClick={this.handleShowModal}
      >
        {(this.props.formType === 'modal-link') ? 'Edit' : ''}
      </i>
    );
  }

  render() {
    if (this.props.formType === 'page') {
      return this.renderContent();
    }
    return (
      <div>
        { this.renderModalLink() }
        { this.renderModal() }
      </div>
    );
  }
}

ProjectResponsibilities.propTypes = {
  formType: PropTypes.string, // one of 'page', 'modal-link', 'modal-icon'
  projectResourceId: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  back: PropTypes.func,
  afterSubmit: PropTypes.func,
  getProjectDetails: PropTypes.func.isRequired,
  backButtonText: PropTypes.string,
  submitButtonText: PropTypes.string,
};

ProjectResponsibilities.defaultProps = {
  formType: 'page',
  projectResourceId: null,
  back: () => {},
  afterSubmit: () => {},
  backButtonText: 'Back',
  submitButtonText: 'Next',
};

export default ProjectResponsibilities;
